class ProductCardCarousel {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector(".card__media-carousel-track");
    this.slides = Array.from(container.querySelectorAll(".card__media-slide"));
    this.prevButton = container.querySelector(".card__media-nav-prev");
    this.nextButton = container.querySelector(".card__media-nav-next");
    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isNavigating = false;
    this.isTouchingQuickAdd = false;

    // Bind methods to this instance
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.navigate = this.navigate.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    this.init();
  }

  init() {
    if (this.slides.length <= 1) {
      console.log("Not enough slides to initialize carousel");
      return;
    }

    // Add event listeners for navigation buttons with explicit binding
    if (this.prevButton) {
      this.prevButton.removeEventListener("click", this.handlePrevClick);
      this.prevButton.addEventListener("click", this.handlePrevClick);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener("click", this.handleNextClick);
      this.nextButton.addEventListener("click", this.handleNextClick);
    }

    // Add touch events for mobile
    this.container.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    this.container.addEventListener("touchmove", this.handleTouchMove, {
      passive: true,
    });
    this.container.addEventListener("touchend", this.handleTouchEnd);

    // Ensure first slide is active
    if (!this.slides.some((slide) => slide.classList.contains("active"))) {
      this.slides[0].classList.add("active");
    }

    // Preload adjacent images
    this.preloadAdjacentImages();
  }

  handleTouchStart(e) {
    // Check if touch started on quick-add button or its children
    const quickAddButton = this.container.querySelector(".quick-add");
    if (
      quickAddButton &&
      (e.target === quickAddButton || quickAddButton.contains(e.target))
    ) {
      this.isTouchingQuickAdd = true;
      return;
    }

    this.isTouchingQuickAdd = false;
    this.touchStartX = e.touches[0].clientX;
    this.touchEndX = this.touchStartX;
  }

  handleTouchMove(e) {
    if (this.isTouchingQuickAdd) return;
    this.touchEndX = e.touches[0].clientX;
  }

  handleTouchEnd() {
    if (this.isTouchingQuickAdd) {
      this.isTouchingQuickAdd = false;
      return;
    }

    const diff = this.touchStartX - this.touchEndX;
    if (Math.abs(diff) > 50) {
      // Minimum swipe distance
      if (diff > 0) {
        // Swipe left
        this.navigate("next");
      } else {
        // Swipe right
        this.navigate("prev");
      }
    }
  }

  handlePrevClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isNavigating) {
      this.navigate("prev");
    }
  }

  handleNextClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isNavigating) {
      this.navigate("next");
    }
  }

  navigate(direction) {
    if (this.isNavigating) return;

    this.isNavigating = true;

    let newIndex = this.currentIndex;

    if (direction === "next") {
      newIndex = (this.currentIndex + 1) % this.slides.length;
    } else {
      newIndex =
        (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    }

    this.goToSlide(newIndex);

    // Reset the navigation flag after a short delay
    setTimeout(() => {
      this.isNavigating = false;
    }, 300);
  }

  goToSlide(index) {
    // Remove active class from all slides first
    this.slides.forEach((slide) => slide.classList.remove("active"));

    // Add active class to new slide
    this.slides[index].classList.add("active");

    this.currentIndex = index;

    // Preload next images
    this.preloadAdjacentImages();
  }

  preloadAdjacentImages() {
    // Preload next image
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    const nextImage = this.slides[nextIndex].querySelector("img");
    if (nextImage && nextImage.dataset.src) {
      nextImage.src = nextImage.dataset.src;
      delete nextImage.dataset.src;
    }

    // Preload previous image
    const prevIndex =
      (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    const prevImage = this.slides[prevIndex].querySelector("img");
    if (prevImage && prevImage.dataset.src) {
      prevImage.src = prevImage.dataset.src;
      delete prevImage.dataset.src;
    }
  }

  // Method to clean up event listeners
  destroy() {
    if (this.prevButton) {
      this.prevButton.removeEventListener("click", this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener("click", this.handleNextClick);
    }
    this.container.removeEventListener("touchstart", this.handleTouchStart);
    this.container.removeEventListener("touchmove", this.handleTouchMove);
    this.container.removeEventListener("touchend", this.handleTouchEnd);
  }
}

// Function to initialize carousels
function initializeCarousels() {
  // First, clean up any existing carousels
  document
    .querySelectorAll(".card__media-carousel.initialized")
    .forEach((carousel) => {
      const instance = carousel._carouselInstance;
      if (instance) {
        instance.destroy();
      }
      carousel.classList.remove("initialized");
    });

  // Initialize new carousels
  const carousels = document.querySelectorAll(
    ".card__media-carousel:not(.initialized)"
  );

  carousels.forEach((carousel) => {
    if (carousel.querySelector(".card__media-slide")) {
      console.log("Initializing carousel");
      carousel.classList.add("initialized");
      carousel._carouselInstance = new ProductCardCarousel(carousel);
    }
  });
}

// Function to reinitialize all product card functionality
function reinitializeAllFunctionality() {
  // Wait for DOM to be ready
  setTimeout(() => {
    initializeCarousels();

    // Reinitialize other functionality
    if (typeof initializeSwatches === "function") {
      initializeSwatches();
    }
    if (typeof initializeQuickAdd === "function") {
      initializeQuickAdd();
    }
  }, 100);
}

// Initialize carousels on page load
document.addEventListener("DOMContentLoaded", initializeCarousels);

// Re-initialize on any custom events that might load new products
document.addEventListener("collection:updated", reinitializeAllFunctionality);

// Handle infinite scroll if enabled
if (window.Ajaxinate) {
  document.addEventListener("ajaxinate:load", reinitializeAllFunctionality);
}

// Listen for cart add events
document.addEventListener("cart:add", reinitializeAllFunctionality);
document.addEventListener("cart:update", reinitializeAllFunctionality);
document.addEventListener("cart:refresh", reinitializeAllFunctionality);

// Listen for section updates from Shopify
document.addEventListener("shopify:section:load", reinitializeAllFunctionality);

// Additional event listeners for cart drawer/modal events
document.addEventListener("cart-drawer:close", reinitializeAllFunctionality);
document.addEventListener("modal:close", reinitializeAllFunctionality);

// Fallback for custom cart events
document.addEventListener("cart_drawer_opened", reinitializeAllFunctionality);
document.addEventListener("cart_drawer_closed", reinitializeAllFunctionality);

// Listen for AJAX completion
document.addEventListener("ajax:complete", reinitializeAllFunctionality);

// Create a mutation observer to watch for new products
const productGridObserver = new MutationObserver((mutations) => {
  let shouldReinitialize = false;

  mutations.forEach((mutation) => {
    // Check if nodes were added
    if (mutation.addedNodes.length > 0) {
      mutation.addedNodes.forEach((node) => {
        // Check if the added node is a product card or contains product cards
        if (
          node.classList &&
          (node.classList.contains("card-wrapper") ||
            node.querySelector(".card-wrapper"))
        ) {
          shouldReinitialize = true;
        }
      });
    }
  });

  if (shouldReinitialize) {
    reinitializeAllFunctionality();
  }
});

// Start observing the product grid
function startObservingGrid() {
  const productGrid = document.getElementById("product-grid");
  if (productGrid) {
    productGridObserver.observe(productGrid, {
      childList: true,
      subtree: true,
    });
  }
}

// Initialize the observer on DOMContentLoaded
document.addEventListener("DOMContentLoaded", () => {
  initializeCarousels();
  startObservingGrid();
});

// Also start observing after any reinitialization
const originalReinitialize = reinitializeAllFunctionality;
reinitializeAllFunctionality = function () {
  originalReinitialize();
  startObservingGrid();
};

// Handle lazy loading events specifically
document.addEventListener("lazyloaded", (e) => {
  const productCard = e.target.closest(".card-wrapper");
  if (productCard) {
    reinitializeAllFunctionality();
  }
});

// Handle scroll-based lazy loading
let lazyLoadTimeout;
window.addEventListener(
  "scroll",
  () => {
    clearTimeout(lazyLoadTimeout);
    lazyLoadTimeout = setTimeout(() => {
      reinitializeAllFunctionality();
    }, 500); // Debounce the reinitialization
  },
  { passive: true }
);
